import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import PortHandle from "./PortHandle";
import { UpdateDeviceLabelContext } from "./UpdateDeviceLabelContext";
import { SelectPortContext } from "./SelectPortContext";
import { portDisplayLabel } from "../constants/signalLabels";

function PortLabel({ port }) {
  return (
    <span className="device-node-port-label">{portDisplayLabel(port)}</span>
  );
}

function formatVoltage(voltageMin, voltageMax) {
  if (!voltageMin && !voltageMax) return null;
  const min = Number(voltageMin);
  const max = Number(voltageMax);
  if (!min && !max) return null;
  if (min === max || !max) return `${min}V`;
  if (!min) return `${max}V`;
  return `${min}\u2013${max}V`;
}

function formatVoltageOptions(options) {
  if (!options || options.length === 0) return null;
  const nums = options.map(Number).filter(Boolean);
  if (nums.length === 0) return null;
  if (nums.length === 1) return `${nums[0]}V`;
  return `${nums[0]}\u2013${nums[nums.length - 1]}V`;
}

function formatCurrent(ma) {
  if (!ma) return null;
  return ma >= 1000 ? `${ma / 1000}A` : `${ma}mA`;
}

function powerLabel(voltage, current) {
  return [voltage, current].filter(Boolean).join(" ");
}

const POLARITY_LABEL = {
  center_negative: "Center −",
  center_positive: "Center +",
  n_a: null,
};

function buildPowerTooltip(parts) {
  return parts.filter(Boolean).join(" · ");
}

function formatAllVoltageOptions(options) {
  if (!options || options.length === 0) return null;
  const nums = options.map(Number).filter(Boolean);
  if (nums.length === 0) return null;
  return nums.map((v) => `${v}V`).join("/");
}

// Signal connector sort order: XLR combo/XLR first (main I/O), then 1/4", etc.
const SIGNAL_CONNECTOR_ORDER = {
  xlr_combo: 0,
  xlr: 1,
  quarter_inch_trs: 2,
  quarter_inch_ts: 3,
  eighth_inch_trs: 4,
  eighth_inch_trrs: 5,
  rca: 6,
  speakon: 7,
  mini_xlr_3pin: 8,
  mini_xlr_4pin: 8,
  midi_din: 9,
  usb_a: 10,
  usb_b: 11,
  usb_b_mini: 12,
  usb_b_micro: 13,
  usb_c: 14,
  ultranet: 15,
  wireless: 16,
  proprietary: 17,
};

// Signal type sort order: audio first, then control/midi, then digital
const SIGNAL_TYPE_ORDER = {
  audio: 0,
  control: 1,
  midi: 2,
  usb_audio: 3,
};

// Power connector sort order: AC first, barrels small to large, then others
const POWER_CONNECTOR_ORDER = {
  ac_outlet: 0,
  barrel_2_1mm: 1,
  barrel_2_5mm: 2,
  rca: 3,
  usb_a: 4,
  usb_c: 5,
  proprietary: 6,
};

function naturalCompare(a, b) {
  return (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });
}

function isMainPort(port) {
  return /^main\b/i.test(port.name || "");
}

function sortSignalPorts(ports) {
  return [...ports].sort((a, b) => {
    const mainA = isMainPort(a) ? 0 : 1;
    const mainB = isMainPort(b) ? 0 : 1;
    if (mainA !== mainB) return mainA - mainB;
    const typeA = SIGNAL_TYPE_ORDER[a.signal_type] ?? 99;
    const typeB = SIGNAL_TYPE_ORDER[b.signal_type] ?? 99;
    if (typeA !== typeB) return typeA - typeB;
    const connA = SIGNAL_CONNECTOR_ORDER[a.connector] ?? 99;
    const connB = SIGNAL_CONNECTOR_ORDER[b.connector] ?? 99;
    if (connA !== connB) return connA - connB;
    return naturalCompare(a.name, b.name);
  });
}

function sortPowerOutPorts(ports) {
  return [...ports].sort((a, b) => {
    const connA = POWER_CONNECTOR_ORDER[a.connector] ?? 99;
    const connB = POWER_CONNECTOR_ORDER[b.connector] ?? 99;
    if (connA !== connB) return connA - connB;
    const voltA = a.voltage_options?.[0] ?? 0;
    const voltB = b.voltage_options?.[0] ?? 0;
    if (voltA !== voltB) return voltA - voltB;
    return (a.name || "").localeCompare(b.name || "");
  });
}

function powerSourceLabel(device) {
  if (device.power_connector === "ac_cable") return "Standard cable";
  const supplied = device.supplied_power_source_ports || [];
  if (supplied.length > 0) return "Included adapter";
  return null;
}

function buildPowerInPort(device) {
  if (!device.power_connector || device.power_connector === "none") return null;
  const voltage = formatVoltage(device.power_voltage_min, device.power_voltage_max);
  const current = formatCurrent(device.power_current_ma);
  return {
    id: `power-in-${device.id}`,
    name: "Power",
    direction: "input",
    signal_type: "power",
    connector: device.power_connector,
    polarity: device.power_polarity,
    powerSpec: powerLabel(voltage, current),
    powerTooltip: buildPowerTooltip([
      powerSourceLabel(device),
      voltage,
      current,
      POLARITY_LABEL[device.power_polarity],
    ]),
  };
}

function buildPowerOutPorts(device) {
  return sortPowerOutPorts(device.power_outputs || []).map((po) => {
    const voltage = formatVoltageOptions(po.voltage_options);
    const allVoltages = formatAllVoltageOptions(po.voltage_options);
    const current = formatCurrent(po.current_ma);
    const displayName = po.count > 1 ? `${po.name} \u00d7${po.count}` : po.name;
    return {
      id: `power-out-${po.id}`,
      name: displayName,
      direction: "output",
      signal_type: "power",
      connector: po.connector,
      polarity: po.polarity,
      powerSpec: powerLabel(voltage, current),
      powerTooltip: buildPowerTooltip([
        allVoltages,
        current,
        POLARITY_LABEL[po.polarity],
        po.isolated ? "Isolated" : null,
      ]),
    };
  });
}

function PhantomBadge({ port }) {
  if (port.phantom_power === "required") {
    return <span className="device-node-phantom" title="Requires 48V phantom power">&#x26A1;</span>;
  }
  if (port.provides_phantom_power === "always_on") {
    return <span className="device-node-phantom" title="Provides phantom power (always on)">&#x26A1;</span>;
  }
  if (port.provides_phantom_power === "switchable") {
    return <span className="device-node-phantom" title="Provides phantom power (switchable)">&#x26A1;</span>;
  }
  return null;
}

export default function DeviceNode({ id, data }) {
  const { device } = data;
  const label = data.label || null;
  const excludedPorts = data.excludedPorts || {};
  const ports = device.device_ports || [];
  const updateDeviceLabel = useContext(UpdateDeviceLabelContext);
  const selectPort = useContext(SelectPortContext);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef(null);

  const inputSignalPorts = sortSignalPorts(ports.filter((p) => p.direction === "input"));
  const outputSignalPorts = sortSignalPorts(ports.filter((p) => p.direction === "output"));

  const isBusPoweredOnly = !device.power_connector && ports.some((p) => p.bus_power);
  const powerInPort = isBusPoweredOnly ? null : buildPowerInPort(device);
  const powerOutPorts = buildPowerOutPorts(device);

  const handleDoubleClick = useCallback(() => {
    if (!updateDeviceLabel) return;
    setDraft(label || "");
    setEditing(true);
  }, [updateDeviceLabel, label]);

  const commitLabel = useCallback(() => {
    setEditing(false);
    if (updateDeviceLabel) {
      updateDeviceLabel(id, draft);
    }
  }, [updateDeviceLabel, id, draft]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        commitLabel();
      } else if (e.key === "Escape") {
        cancelEdit();
      }
    },
    [commitLabel, cancelEdit],
  );

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  return (
    <div className="device-node">
      {powerInPort && (
        <div className="device-node-ports-top">
          <div className="device-node-port device-node-port--top">
            <PortHandle port={powerInPort} position="top" />
            <span className="device-node-port-label">{powerInPort.name}</span>
            {powerInPort.powerSpec && (
              <span className="device-node-power-spec">{powerInPort.powerSpec}</span>
            )}
          </div>
        </div>
      )}

      <div
        className="device-node-header"
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="device-node-name">{device.name}</div>
        {device.manufacturer && (
          <div className="device-node-manufacturer">{device.manufacturer}</div>
        )}
        {editing ? (
          <input
            ref={inputRef}
            className="device-node-label-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitLabel}
            onKeyDown={handleKeyDown}
          />
        ) : label ? (
          <div className="device-node-label">{label}</div>
        ) : (
          hovered && updateDeviceLabel && (
            <div className="device-node-label device-node-label-placeholder">Add label…</div>
          )
        )}
      </div>

      <div className="device-node-body">
        <div className="device-node-ports-left">
          {inputSignalPorts.map((port) => {
            const portKey = String(port.id);
            const isExcluded = Object.prototype.hasOwnProperty.call(excludedPorts, portKey);
            const exclusionNote = isExcluded ? excludedPorts[portKey] : null;
            const portClass = `device-node-port device-node-port--left${isExcluded ? " device-node-port--excluded" : ""}`;
            const title = isExcluded ? `Excluded: ${exclusionNote || "No reason given"}` : undefined;
            const handlePortClick = selectPort ? (e) => { e.stopPropagation(); selectPort(id, String(port.id)); } : undefined;
            return (
              <div key={port.id} className={portClass} title={title} onClick={handlePortClick}>
                <PortHandle port={port} position="left" excluded={isExcluded} />
                <PortLabel port={port} />
                <PhantomBadge port={port} />
              </div>
            );
          })}
        </div>
        <div className="device-node-ports-right">
          {outputSignalPorts.map((port) => {
            const portKey = String(port.id);
            const isExcluded = Object.prototype.hasOwnProperty.call(excludedPorts, portKey);
            const exclusionNote = isExcluded ? excludedPorts[portKey] : null;
            const portClass = `device-node-port device-node-port--right${isExcluded ? " device-node-port--excluded" : ""}`;
            const title = isExcluded ? `Excluded: ${exclusionNote || "No reason given"}` : undefined;
            const handlePortClick = selectPort ? (e) => { e.stopPropagation(); selectPort(id, String(port.id)); } : undefined;
            return (
              <div key={port.id} className={portClass} title={title} onClick={handlePortClick}>
                <PhantomBadge port={port} />
                <PortLabel port={port} />
                <PortHandle port={port} position="right" excluded={isExcluded} />
              </div>
            );
          })}
        </div>
      </div>

      {powerOutPorts.length > 0 && (
        <div className="device-node-ports-bottom">
          {powerOutPorts.map((port) => (
            <div key={port.id} className="device-node-port device-node-port--bottom">
              <PortHandle port={port} position="bottom" />
              <PortLabel port={port} />
              {port.powerSpec && (
                <span className="device-node-power-spec">{port.powerSpec}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
